// AP/AR Approval Decision (docs/workflow.md step 6, issue 3 2026-08-11).
// Modeled on ImportControlScreen.tsx: a single-status, click-into-each queue
// with no bulk actions, because AP/AR now carries the review work Import
// Control used to do for every invoice — Import Control narrowed to settling
// an ambiguous company alone (owner ruling 4), so most invoices skip it
// entirely and arrive here still carrying whatever flags Matching raised.
//
// AP/AR is MANDATORY, not present-if-staffed like the three tiers below it: an
// invoice never routes around it, so a company with nobody appointed `ap-ar`
// freezes here — the Pipeline dialog's warning icon on this node exists for
// exactly that.
//
// Company-scoped, exactly like Department/Director/Oversight: the queue is
// whatever the caller's `ap-ar` seat covers, and `canDecide` is the same
// per-row hint those screens use. Two outcomes, no deny (same as Import
// Control): `approved` releases the invoice — the company's approvers gain
// access — and `archived not an invoice` matches a document that turns out not
// to be a payable at all.
//
// The line-item grid shows ONLY "What is it?" (owner ruling, issue 3) — no
// Account picker, no "Who is it for?": the cost center is Department
// Approval's business or later, and the composed account belongs to Export
// Control and Manual Entry. `editExpenseTypes` alone (no `editCostCenters`, no
// `editAccounts`) is what narrows `InvoiceLineItemsTable` to that one column.
import { useMemo, useState } from 'react'
import { Body1, Button, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { ScreenTitle } from '../components/ScreenTitle'
import type { Invoice } from '../lib/types'
import { DecisionOutcome, DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { createDecision } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, canPreviewPdf, type Session } from '../lib/identity'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

const NOT_AUTHORIZED_REASON =
  "Only an AP/AR holder assigned to this invoice's own company can decide on it at this step. You can view it, but your decision would be refused."

export function ApArApprovalScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingApAr], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  const decidableIds = useMemo(
    () => new Set(filtered.filter((i) => canDecide(session, DecisionStep.ApAr, i)).map((i) => i.id)),
    [filtered, session],
  )

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [archiving, setArchiving] = useState<Invoice | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  function settle() {
    setOpenInvoice(null)
    setArchiving(null)
    void reload()
    onQueuesChanged?.()
  }

  async function approve(invoice: Invoice) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.Approved,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Invoice approved and released to its company's approvers</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function archiveNotAnInvoice(invoice: Invoice, reason: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ApAr,
        decision: DecisionOutcome.ArchivedNotAnInvoice,
        reason,
        row_version: invoice.row_version,
      })
      settle()
      dispatchToast(
        <Toast>
          <ToastTitle>Archived — Not an Invoice</ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>AP/AR Approval</ScreenTitle>
        <Body1 className={s.sub}>
          For each invoice, resolve any outstanding flags, confirm the amounts reconcile and every line item
          has an expense type, then approve to release it to its company's approvers.
        </Body1>
      </div>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading held invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue
          invoices={filtered}
          showFlags
          onOpen={setOpenInvoice}
          rowActions={(inv) => {
            const decidable = decidableIds.has(inv.id)
            return (
              <>
                <Button
                  size="small"
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  title={decidable ? 'Approve' : NOT_AUTHORIZED_REASON}
                  aria-label="Approve"
                  disabled={submitting || !decidable}
                  onClick={() => approve(inv)}
                />
                <Button
                  size="small"
                  className={btn.deny}
                  icon={<DismissRegular />}
                  title={decidable ? 'Archive — not an invoice' : NOT_AUTHORIZED_REASON}
                  aria-label="Archive — not an invoice"
                  disabled={submitting || !decidable}
                  onClick={() => setArchiving(inv)}
                />
              </>
            )
          }}
        />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={canPreviewPdf(session)}
        user={user}
        editExpenseTypes
        onChanged={() => void reload({ silent: true })}
        onClose={() => setOpenInvoice(null)}
        actions={
          openInvoice &&
          (() => {
            const decidable = canDecide(session, DecisionStep.ApAr, openInvoice)
            return (
              <>
                <Button
                  className={btn.approve}
                  icon={<CheckmarkRegular />}
                  disabled={submitting || !decidable}
                  title={decidable ? undefined : NOT_AUTHORIZED_REASON}
                  onClick={() => approve(openInvoice)}
                >
                  Approve
                </Button>
                <Button
                  className={btn.deny}
                  icon={<DismissRegular />}
                  disabled={submitting || !decidable}
                  title={decidable ? undefined : NOT_AUTHORIZED_REASON}
                  onClick={() => setArchiving(openInvoice)}
                >
                  Not An Invoice
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!archiving}
        title="Archive — not an invoice?"
        body="This document will be archived as not an invoice. This comment is recorded on the record and is visible in the decision history."
        requireReason
        reasonLabel="Reason"
        confirmLabel="Archive"
        destructive
        submitting={submitting}
        onCancel={() => setArchiving(null)}
        onConfirm={(reason) => archiving && archiveNotAnInvoice(archiving, reason)}
      />
    </div>
  )
}
